import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapPin, Search, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useMunicipios, type Municipio } from '@/hooks/useMunicipios';

interface MunicipioAutocompleteProps {
  value: string; // codigo_ibge
  onChange: (codigoIbge: string) => void;
  onMunicipioSelect?: (municipio: Municipio | null) => void;
  /** Filter options to only these IBGE codes */
  filterCodes?: string[];
  placeholder?: string;
}

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const levenshteinDistance = (a: string, b: string) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const rows = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    let previousDiagonal = rows[0];
    rows[0] = i;

    for (let j = 1; j <= b.length; j++) {
      const temp = rows[j];
      rows[j] = Math.min(
        rows[j] + 1,
        rows[j - 1] + 1,
        previousDiagonal + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      previousDiagonal = temp;
    }
  }

  return rows[b.length];
};

const getMunicipioSearchScore = (municipio: Municipio, query: string) => {
  const nome = normalizeText(municipio.nome);
  const uf = normalizeText(municipio.uf);
  const fullLabel = `${nome} ${uf}`;

  if (!query) return 0;
  if (nome === query || fullLabel === query) return 100;
  if (nome.startsWith(query)) return 90;
  if (nome.includes(query)) return 80;
  if (uf === query || fullLabel.includes(query)) return 70;

  if (query.length >= 4) {
    const distanceToName = levenshteinDistance(query, nome);
    const distanceToPrefix = levenshteinDistance(query, nome.slice(0, query.length));
    const bestDistance = Math.min(distanceToName, distanceToPrefix);
    const maxAcceptedDistance = query.length >= 10 ? 2 : 1;

    if (bestDistance <= maxAcceptedDistance) {
      return 60 - bestDistance * 10;
    }
  }

  return -1;
};

export const MunicipioAutocomplete: React.FC<MunicipioAutocompleteProps> = ({
  value,
  onChange,
  onMunicipioSelect,
  filterCodes,
  placeholder = 'Pesquisar município por nome ou UF...',
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: municipios = [] } = useMunicipios();

  // Apply optional filter
  const availableMunicipios = useMemo(() => {
    if (!filterCodes || filterCodes.length === 0) return municipios;
    const codeSet = new Set(filterCodes);
    return municipios.filter(m => codeSet.has(m.codigo_ibge));
  }, [municipios, filterCodes]);

  const selectedMunicipio = useMemo(
    () => availableMunicipios.find(m => m.codigo_ibge === value) || null,
    [availableMunicipios, value]
  );

  useEffect(() => {
    if (selectedMunicipio && !isOpen) {
      setSearch(`${selectedMunicipio.nome} – ${selectedMunicipio.uf}`);
    } else if (!value && !isOpen) {
      setSearch('');
    }
  }, [selectedMunicipio, value, isOpen]);

  const filtered = useMemo(() => {
    if (!search.trim()) return availableMunicipios.slice(0, 20);

    const query = normalizeText(search);

    return availableMunicipios
      .map(municipio => ({
        municipio,
        score: getMunicipioSearchScore(municipio, query),
      }))
      .filter(item => item.score >= 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.municipio.nome.localeCompare(b.municipio.nome, 'pt-BR');
      })
      .slice(0, 15)
      .map(item => item.municipio);
  }, [availableMunicipios, search]);

  // Click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (mun: Municipio) => {
    onChange(mun.codigo_ibge);
    onMunicipioSelect?.(mun);
    setSearch(`${mun.nome} – ${mun.uf}`);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    onMunicipioSelect?.(null);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-8 pr-8"
          placeholder={placeholder}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              Nenhum município encontrado
            </div>
          ) : (
            filtered.map(mun => (
              <button
                key={mun.codigo_ibge}
                type="button"
                className={cn(
                  'flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors text-left',
                  mun.codigo_ibge === value && 'bg-accent/50'
                )}
                onClick={() => handleSelect(mun)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium">{mun.nome}</span>
                  <span className="text-muted-foreground">– {mun.uf}</span>
                </div>
                {mun.codigo_ibge === value && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      )}

      {selectedMunicipio && (
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          <span>{selectedMunicipio.nome} – {selectedMunicipio.uf}</span>
        </div>
      )}
    </div>
  );
};
